/**
 * Push notification helper — shared by Profile toggle, dashboard banner,
 * chat reminder, and the silent auto-subscribe hook.
 *
 * Browser rule: Notification.requestPermission() MUST be called from a user
 * gesture. Silent flows below only run when permission is ALREADY 'granted'.
 */
import { apiClient } from "../App";

export const PUSH_DISMISS_KEY = "vivo:push_prompt_dismissed_at";
const DISMISS_TTL_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

/** Quick capability check — returns false on unsupported browsers (older Safari, in-app webviews). */
export const isPushSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

/** Decode the VAPID public key (URL-safe base64) into a Uint8Array. */
const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const buf = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) buf[i] = raw.charCodeAt(i);
  return buf;
};

/** Encode a binary key from PushSubscription.getKey to URL-safe base64. */
const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; ++i) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
};

/** True if the user has explicitly dismissed the auto-prompt within the TTL. */
export const isPromptDismissed = () => {
  try {
    const v = localStorage.getItem(PUSH_DISMISS_KEY);
    if (!v) return false;
    const ts = parseInt(v, 10);
    return Number.isFinite(ts) && Date.now() - ts < DISMISS_TTL_MS;
  } catch {
    return false;
  }
};

export const dismissPrompt = () => {
  try { localStorage.setItem(PUSH_DISMISS_KEY, String(Date.now())); } catch {}
};

/**
 * Subscribe the browser to push and tell the server.
 * Caller must already hold (or just-acquired via gesture) a 'granted' permission.
 * Returns true on success.
 */
export const subscribeAndRegister = async () => {
  if (!isPushSupported()) return false;
  try {
    const { data } = await apiClient.get("/push/vapid-key");
    if (!data?.publicKey) return false;

    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
      });
    }

    await apiClient.post("/push/subscribe", {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: arrayBufferToBase64(subscription.getKey("p256dh")),
        auth: arrayBufferToBase64(subscription.getKey("auth")),
      },
    });
    return true;
  } catch (e) {
    console.warn("[push] subscribeAndRegister failed:", e);
    return false;
  }
};

/**
 * Silent variant: ONLY succeeds if permission is already 'granted'. No prompt
 * shown to the user. Use on app load to recover the subscription after data
 * was cleared, on a new device that already has OS-level permission, etc.
 */
export const trySilentAutoSubscribe = async () => {
  if (!isPushSupported()) return false;
  if (Notification.permission !== "granted") return false;
  return subscribeAndRegister();
};

/**
 * Interactive: pop the browser permission dialog (must be from a user gesture)
 * then register. Returns 'granted' | 'denied' | 'unsupported' | 'error'.
 */
export const requestPermissionAndSubscribe = async () => {
  if (!isPushSupported()) return "unsupported";
  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return "denied";
    const ok = await subscribeAndRegister();
    return ok ? "granted" : "error";
  } catch {
    return "error";
  }
};
